import { getConfig, getEnv } from "@greeter-guest/utility/ConfigHooks";
import { GradientButton, LoadingOverlay } from "@greeter/matter";
import { useSearchParams } from "@greeter/url";
import { doNothing, doNothingAsync, Lazy } from "@greeter/util";
import { useIonToast } from "@ionic/react";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { FormEvent, useCallback, useEffect, useState } from "react";

export class StripeLoader {
  static lazyStripePromise: Lazy<ReturnType<typeof loadStripe>> = new Lazy(
    () => {
      const config = getConfig();
      return loadStripe(config.stripe.publishableKey, {
        locale: "da",
      });
    }
  );
}

const declineError = [
  "authentication_required",
  "approve_with_id",
  "call_issuer",
  "card_not_supported",
  "card_velocity_exceeded",
  "currency_not_supported",
  "do_not_honor",
  "do_not_try_again",
  "duplicate_transaction",
  "expired_card",
  "fraudulent",
  "generic_decline",
  "incorrect_cvc",
  "incorrect_number",
  "incorrect_pin",
  "incorrect_zip",
  "insufficient_funds",
  "invalid_account",
  "invalid_amount",
  "invalid_cvc",
  "invalid_expiry_month",
  "invalid_expiry_year",
  "invalid_number",
  "processing_error",
  "reenter_transaction",
  "restricted_card",
  "revocation_of_all_authorizations",
  "revocation_of_authorization",
  "security_violation",
  "service_not_allowed",
  "stolen_card",
  "stop_payment_order",
  "testmode_decline",
  "transaction_not_allowed",
  "try_again_later",
  "withdrawal_count_limit_exceeded",
  "pickup_card",
  "online_or_offline_pin_required",
  "offline_pin_required",
  "not_permitted",
  "issuer_not_available",
  "lost_card",
  "merchant_blacklist",
  "new_account_information_available",
  "no_action_taken",
  "not_permitted",
  "pin_try_exceeded",
] as const;

// https://stripe.com/docs/declines/codes
export type DeclineError = (typeof declineError)[number];

export class MissingCardElementError extends Error {}
export class MissingStripeError extends Error {}

export function translateStripeErrorToDanish(err: DeclineError): string {
  switch (err) {
    case "authentication_required":
      return "Dit kort er blevet afvist og kræver godkendelse.";
    case "approve_with_id":
      return "Dit kort kunne ikke autoriseres.";
    case "card_not_supported":
      return "Kortet er ikke understøttet.";
    case "withdrawal_count_limit_exceeded":
    case "card_velocity_exceeded":
      return "Ikke nok penge på kortet. Dit kort kan ikke opfylde det ønskede beløb.";
    case "currency_not_supported":
      return "Danske kroner er ikke understøttet af kortet.";
    case "duplicate_transaction":
      return "En duplikat transaktion var været lavet indenfor kort tid.";
    case "expired_card":
      return "Dit kort er udløbet.";
    case "fraudulent":
      return "Dit kort blev afvist da der er opdaget mistænksom aktivitet.";
    case "stolen_card":
    case "restricted_card":
    case "pickup_card":
    case "lost_card":
    case "merchant_blacklist":
    case "generic_decline":
      return "Dit kort blev afvist.";
    case "incorrect_cvc":
    case "incorrect_number":
    case "incorrect_pin":
    case "incorrect_zip":
    case "invalid_account":
    case "invalid_amount":
    case "invalid_cvc":
    case "invalid_expiry_month":
    case "invalid_expiry_year":
    case "invalid_number":
      return "Forkerte kort-oplysninger indtastet.";
    case "insufficient_funds":
      return "Der er ikke nok danske dollars på kortet.";
    case "processing_error":
      return "En fejl forekom under transaktionen. Prøv igen om et øjeblik.";
    case "testmode_decline":
      return "Et Stripe test kort er blevet brugt. Prøv med et rigtigt kort istedet.";
    case "try_again_later":
      return "Kortet er blevet afvist af ukendte årsager. Prøv igen senere.";
    case "online_or_offline_pin_required":
    case "offline_pin_required":
      return "Kortet er blevet afvist da det mangler en pin-kode.";
    case "not_permitted":
      return "Kortet er ikke tilladt denne handling.";
    case "issuer_not_available":
      return "Kunne ikke få forbindelse til banken. Prøv igen senere.";
    case "new_account_information_available":
      return "Kontoen kortet er tilknyttet er ikke gyldig.";
    case "pin_try_exceeded":
      return "Du har prøvet at indtaste din pin-kode for mange gange. Kontakt din bank.";
    case "do_not_honor":
    case "do_not_try_again":
    case "reenter_transaction":
    case "revocation_of_all_authorizations":
    case "revocation_of_authorization":
    case "security_violation":
    case "service_not_allowed":
    case "stop_payment_order":
    case "transaction_not_allowed":
    case "no_action_taken":
    case "call_issuer":
    default:
      return `Dit kort blev afvist af en ukendt årsag: ${err}`;
  }
}

type CardErrors =
  | "charge_expired_for_capture"
  | "expired_card"
  | "incorrect_cvc"
  | "incorrect_number"
  | "invalid_card_type"
  | "invalid_cvc"
  | "invalid_number";

export function toDeclineError(stripeErrCode?: string): DeclineError {
  return declineError.find((e) => e === stripeErrCode) ?? "processing_error";
}

export function useStripeErrorHandler() {
  const [supportToast] = useIonToast();
  return useCallback(
    (err?: string) =>
      supportToast({
        mode: "ios",
        message: translateStripeErrorToDanish(toDeclineError(err)),
        buttons: [
          { text: "Ok" },
          {
            text: "💬 Hjælp",
            handler() {
              window.dispatchEvent(new CustomEvent("greeter:open-support"));
            },
          },
        ],
      }),
    [supportToast]
  );
}

/**
 * https://stripe.com/docs/declines/codes
 * https://stripe.com/docs/error-codes
 */
export class StripeDeclineError extends Error {
  error: DeclineError;

  constructor(error: DeclineError) {
    super();

    this.error = error;
  }
}

export type CheckoutFormProps = React.PropsWithChildren & {
  onPay?: () => void | Promise<void>;
  onError?: (error: DeclineError) => void;
  onSuccess?: () => void;
  token: string;
  redirectUrl?: () => string;
};

export function CheckoutForm({
  onPay = doNothingAsync,
  onError = doNothing,
  onSuccess = doNothing,
  redirectUrl,
}: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const env = getEnv();
  const displayStripeToast = useStripeErrorHandler();
  const [confirming, setConfirming] = useState(false);

  const [errorMessages, setErrorMessages] = useState<string[]>([]);

  const searchParams = useSearchParams();
  const [checked, setChecked] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    if (checked) return;
    if (!stripe) return;

    async function checkPayment() {
      const stripePaymentClientSecret = searchParams.get(
        "payment_intent_client_secret"
      );

      if (!stripePaymentClientSecret) return;

      setCheckingPayment(true);

      const paymentIntent = await stripe?.retrievePaymentIntent(
        stripePaymentClientSecret
      );

      console.debug("stripe payment intent", paymentIntent);
      if (paymentIntent?.error) {
        try {
          onError(toDeclineError(paymentIntent.error.code));
        } catch {}
      } else if (paymentIntent?.paymentIntent?.status === "requires_capture") {
        try {
          onSuccess();
        } catch {}
      }
      setCheckingPayment(false);
      setChecked(true);
    }

    checkPayment();
  }, [stripe, onSuccess, checked]);

  async function pay(ev: FormEvent) {
    const tag = `[${pay.name}]`;
    ev.preventDefault();
    setConfirming(true);
    console.debug(tag, "triggered", ev);
    let onPayPromise = onPay();

    if (!elements) {
      setConfirming(false);
      throw new MissingCardElementError();
    }

    if (!stripe) {
      setConfirming(false);
      throw new MissingStripeError();
    }

    try {
      const result = await stripe.confirmPayment({
        elements: elements,
        redirect: "if_required",
        confirmParams: {
          return_url: redirectUrl?.call({}),
        },
      });

      try {
        await onPayPromise;
      } catch (error) {
        setConfirming(false);
        console.warn(tag, "failed to run onPay", error);
        throw error;
      }

      if (result.error) {
        onError
          ? onError(toDeclineError(result.error.decline_code))
          : displayStripeToast(result.error.decline_code);
        setErrorMessages([
          ...errorMessages,
          JSON.stringify(result.error, null, 4),
        ]);
      } else {
        onSuccess();
      }
    } catch (error) {
      console.warn(`[${pay.name}] failed to confirmPayment`, error);
    } finally {
      setConfirming(false);
    }
  }

  return (
    <form
      onSubmit={pay}
      style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
    >
      <PaymentElement
        id="payment"
        options={{
          wallets: { applePay: "auto", googlePay: "auto" },
          business: { name: "Greeter" },
          paymentMethodOrder: ["mobilepay", "apple_pay", "google_pay", "card"],
          layout: "tabs",
        }}
      />
      <GradientButton
        style={{ borderRadius: 10, fontSize: "1.5rem" }}
        disabled={!stripe}
      >
        Betal
      </GradientButton>
      <LoadingOverlay message={confirming && "Betaler..."} />
      <LoadingOverlay
        message={checkingPayment && !checked && "Tjekker betalingen..."}
      />
      {env === "dev" && (
        <pre
          style={{
            width: "100%",
            height: "100%",
            padding: "1rem",
            color: "green",
            backgroundColor: "black",
            textAlign: "left",
            boxSizing: "border-box",
            maxWidth: "100vw",
          }}
        >
          {errorMessages}
        </pre>
      )}
    </form>
  );
}

export type CheckoutProps = CheckoutFormProps & {
  token?: string;
  redirectUrl?: () => string;
};

export function Checkout({ token, ...formProps }: CheckoutProps) {
  return (
    <Elements
      key={token}
      stripe={StripeLoader.lazyStripePromise.value}
      options={{ clientSecret: token, appearance: { theme: "night" } }}
    >
      <CheckoutForm token={token} {...formProps} />
    </Elements>
  );
}
